import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { getFormValues, isInvalid } from 'redux-form';
import { initialState } from './reducer';
import {
	AUTOPARK_TRANSPORT_FORM_NAME,
	TRANSPORT_TABLE_ROWS_NAMES,
	TRANSPORT_TARIFF_TYPES_TABLE_ROWS_NAMES,
} from './constants';
import { thousandSeparator } from '../reqs-filter/utils';

export const selectAutoparkTransportModalDomain = state => state.autoparkTransportModalReducer || initialState;

export const selectRouterDomain = state => state.router || {};

export const getIsAutoparkTransportInfoLoadingSelector = () =>
	createSelector(
		selectAutoparkTransportModalDomain,
		({
			isAutoparkTransportInfoLoading,
			isAutoparkTransportCreating,
			isAutoparkTransportUpdating,
			isAutoparkTransportDeleting,
		}) =>
			isAutoparkTransportInfoLoading ||
			isAutoparkTransportCreating ||
			isAutoparkTransportUpdating ||
			isAutoparkTransportDeleting,
	);

export const getAutoparkTransportInfoSelector = () =>
	createSelector(selectAutoparkTransportModalDomain, ({ autoparkTransportInfo }) => {
		if (!autoparkTransportInfo) {
			return [];
		}

		return Object.entries(TRANSPORT_TABLE_ROWS_NAMES).reduce((result, [key, value]) => {
			if (['is_allow', 'is_deny_refund'].includes(key)) {
				result.push({
					id: key,
					title: value,
					value: autoparkTransportInfo[key] ? 'Да' : 'Нет',
				});

				return result;
			}

			if (key === 'type') {
				result.push({
					id: key,
					title: value,
					value: autoparkTransportInfo.type.name,
				});

				return result;
			}

			if (key === 'weight') {
				result.push({
					id: key,
					title: value,
					value: `${thousandSeparator(autoparkTransportInfo.weight)} кг`,
				});

				return result;
			}

			if (key === 'volume') {
				result.push({
					id: key,
					title: value,
					value: `${thousandSeparator(autoparkTransportInfo.volume)} м³`,
				});

				return result;
			}

			if (key === 'count') {
				result.push({
					id: key,
					title: value,
					value: `${thousandSeparator(autoparkTransportInfo.count)} шт`,
				});

				return result;
			}

			if (key === 'address') {
				result.push({
					id: key,
					title: value,
					value: autoparkTransportInfo.address?.name || '---',
				});

				return result;
			}

			if (key === 'author') {
				result.push({
					id: key,
					title: value,
					value: {
						name: `${autoparkTransportInfo.user?.last_name || '-'} ${
							autoparkTransportInfo.user?.first_name || '-'
						}`,
						role: autoparkTransportInfo.user?.role.name || '-',
					},
				});

				return result;
			}

			if (key === 'tariffs') {
				autoparkTransportInfo.tariffs?.forEach(tariff => {
					result.push({
						id: `tariff_type_${tariff.type.id}`,
						title: tariff.type.name,
						value: '',
						group: tariff.type.name,
					});

					Object.entries(TRANSPORT_TARIFF_TYPES_TABLE_ROWS_NAMES).forEach(([tariffKey, tariffTitle]) => {
						if (tariffKey === 'transport_stage_length' || tariffKey === 'performer_stage_length') {
							return;
						}

						if (tariffKey === 'performer_cost_per_unit') {
							const values = [];

							if (tariff.performer_cost_per_unit) {
								values.push(`${thousandSeparator(tariff.performer_cost_per_unit)} руб/ч`);
							}

							if (tariff.performer_stage_length) {
								values.push(`за первые ${thousandSeparator(tariff.performer_stage_length)} ч`);
							}

							result.push({
								id: `${tariffKey}_${tariff.type?.id}`,
								title: tariffTitle,
								value: values.length ? values.join(' ') : '---',
							});

							return;
						}

						if (tariffKey === 'transport_cost_per_unit') {
							const values = [];

							if (tariff.transport_cost_per_unit) {
								values.push(`${thousandSeparator(tariff.transport_cost_per_unit)} руб/км`);
							}

							if (tariff.transport_stage_length) {
								values.push(`за первые ${thousandSeparator(tariff.transport_stage_length)} км`);
							}

							result.push({
								id: `${tariffKey}_${tariff.type?.id}`,
								title: tariffTitle,
								value: values.length ? values.join(' ') : '---',
							});

							return;
						}

						const tariffValue =
							tariff[tariffKey] != null ? `${thousandSeparator(tariff[tariffKey])} руб` : '---';

						result.push({
							id: `${tariffKey}_${tariff.type?.id}`,
							title: tariffTitle,
							value: tariffValue,
						});
					});
				});

				return result;
			}

			result.push({
				id: key,
				title: value,
				value: autoparkTransportInfo[key] ?? '-',
			});

			return result;
		}, []);
	});

export const getAutoparkTransportNameSelector = () =>
	createSelector(selectAutoparkTransportModalDomain, ({ autoparkTransportInfo }) => {
		return autoparkTransportInfo ? autoparkTransportInfo.name : '';
	});

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'transport');
	});

export const getActiveAutoparkIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return query?.id;
	});

export const getActiveAutoparkTransportIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return query?.modal === 'transport' ? query?.transportId : null;
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'transport' && query?.transportId && query?.tab === 'audit');
	});

export const getIsAutoparkTransportAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'transport' && query?.mode === 'add');
	});

export const getIsAutoparkTransportEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'transport' && query?.mode === 'edit');
	});

export const getFormInitialValuesSelector = () =>
	createSelector(
		selectAutoparkTransportModalDomain,
		({ autoparkTransportInfo, autoparkTransportTariffTypesList }) => {
			if (!autoparkTransportInfo) {
				return {
					is_allow: true,
					is_deny_refund: false,
					tariffs: autoparkTransportTariffTypesList.map(type => ({
						type_id: type.id,
					})),
				};
			}

			return {
				...autoparkTransportInfo,
				type_id: autoparkTransportInfo.type.id,
				address_id: autoparkTransportInfo.address?.id,
				tariffs: autoparkTransportInfo.tariffs.map(tariff => ({
					...tariff,
					type_id: tariff.type.id,
				})),
			};
		},
	);

export const getIsTransportTypesListLoadingSelector = () =>
	createSelector(
		selectAutoparkTransportModalDomain,
		({ isTransportTypesListLoading }) => isTransportTypesListLoading,
	);

export const getTransportTypesListOptionsSelector = () =>
	createSelector(selectAutoparkTransportModalDomain, ({ transportTypesList }) =>
		transportTypesList.map(type => ({
			key: type.id,
			value: type.id,
			text: type.name,
		})),
	);

export const getAutoparkTransportTariffTypesListSelector = () =>
	createSelector(
		selectAutoparkTransportModalDomain,
		({ autoparkTransportTariffTypesList }) => autoparkTransportTariffTypesList,
	);

export const getIsAutoparkTransportTariffTypesListLoadingSelector = () =>
	createSelector(
		selectAutoparkTransportModalDomain,
		({ isAutoparkTransportTariffTypesListLoading }) => isAutoparkTransportTariffTypesListLoading,
	);

export const getAddressesListOptionsSelector = () =>
	createSelector(selectAutoparkTransportModalDomain, ({ addressesList }) => [
		{ key: 'none', value: 'none', text: 'Нет' },
		...addressesList.map(address => ({
			key: address.id,
			value: address.id,
			text: address.name,
		})),
	]);

export const getIsAddressesListLoadingSelector = () =>
	createSelector(selectAutoparkTransportModalDomain, ({ isAddressesListLoading }) => isAddressesListLoading);

export const getDataForSaveSelector = () =>
	createSelector(getFormValues(AUTOPARK_TRANSPORT_FORM_NAME), formValues => {
		return {
			...formValues,
			address_id: formValues?.address_id === 'none' ? null : formValues?.address_id,
		};
	});

export const getFormIsInvalidSelector = () =>
	createSelector(isInvalid(AUTOPARK_TRANSPORT_FORM_NAME), isInvalid => isInvalid);
